import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  LocalPrintshop as LocalPrintshopIcon,
} from "@mui/icons-material";
import api from "../../../api/axiosConfig";
import { endpoints } from "../../../api/endpoints";
import { getApiErrorMessage } from "../../../utils/apiError";

type BatchStatus = "Pending" | "Ready" | "Failed" | "Active" | "Printed";

interface BatchDetailResponse {
  preview_url?: string;
  download_url?: string;
  expires_in_seconds?: number;
}

export interface BatchPreviewBatch {
  id: string;
  batchId: string;
  quantity: number;
  remarks: string;
  createdAt: string;
  status: BatchStatus;
  path: string | null;
}

interface ViewBatchModalProps {
  open: boolean;
  onClose: () => void;
  batch: BatchPreviewBatch | null;
}

export const ViewBatchModal: React.FC<ViewBatchModalProps> = ({
  open,
  onClose,
  batch,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [batchDetail, setBatchDetail] = useState<BatchDetailResponse | null>(
    null,
  );

  useEffect(() => {
    if (!open || !batch?.id) {
      setBatchDetail(null);
      setErrorMessage(null);
      return;
    }

    const controller = new AbortController();

    const fetchBatchDetails = async () => {
      setIsLoading(true);
      setErrorMessage(null);
      setBatchDetail(null);

      try {
        const response = await api.get<BatchDetailResponse>(
          endpoints.qrTemplates.downloadBulkWithQrByBatchId(batch.id),
          { signal: controller.signal },
        );

        setBatchDetail(response.data || null);
      } catch (error: any) {
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
          return;
        }

        setErrorMessage(
          getApiErrorMessage(
            error,
            "Failed to load batch preview. Please try again.",
          ),
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchBatchDetails();

    return () => {
      controller.abort();
    };
  }, [batch?.id, open]);

  const pdfUrl = batchDetail?.preview_url || null;
  const heading = batch?.batchId || "QR Batch";

  const handlePrint = async () => {
    if (!batch?.id || !batchDetail?.download_url) {
      return;
    }

    setIsDownloading(true);

    try {
      const downloadLink = document.createElement("a");
      downloadLink.href = batchDetail.download_url;
      downloadLink.download = `${batch.batchId}.pdf`;
      downloadLink.target = "_blank";
      downloadLink.rel = "noopener noreferrer";
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (error: any) {
      setErrorMessage(
        getApiErrorMessage(error, "Failed to download PDF. Please try again."),
      );
      console.error("PDF download error:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 1,
            overflow: "hidden",
            minHeight: 680,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "#2E7D32",
          color: "#FFFFFF",
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}
        >
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 600 }}>
            {heading} - QR Codes
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: "#FFFFFF" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2.5, bgcolor: "#FAFAFA" }}>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Box
          sx={{
            bgcolor: "#FFFFFF",
            border: "1px solid #E0E0E0",
            borderRadius: 1,
            minHeight: 520,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isLoading ? (
            <CircularProgress size={32} />
          ) : pdfUrl ? (
            <Box
              component="iframe"
              src={pdfUrl}
              title={`${heading} QR preview`}
              sx={{
                width: "100%",
                height: 520,
                border: 0,
                bgcolor: "#FFFFFF",
              }}
            />
          ) : (
            <Box sx={{ px: 3, textAlign: "center" }}>
              <Typography sx={{ color: "#666666", fontWeight: 500, mb: 1 }}>
                PDF preview is not available for this batch.
              </Typography>
              <Typography sx={{ color: "#8A8A8A" }}>
                Preview URL is not available or has expired for this batch.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 1.75,
          justifyContent: "flex-end",
          gap: 1.5,
        }}
      >
        <Button
          onClick={onClose}
          sx={{ textTransform: "none", color: "#2E7D32", fontWeight: 500 }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          startIcon={
            isDownloading ? (
              <CircularProgress size={18} sx={{ color: "#FFFFFF" }} />
            ) : (
              <LocalPrintshopIcon />
            )
          }
          onClick={handlePrint}
          disabled={
            !batch?.id ||
            !batchDetail?.download_url ||
            isDownloading ||
            isLoading
          }
          sx={{
            textTransform: "none",
            bgcolor: "#2E7D32",
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#27642A",
              boxShadow: "none",
            },
          }}
        >
          {isDownloading ? "Downloading..." : "Print PDF"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewBatchModal;
